import { useEffect, useRef, useState } from "react";
import { Select } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  fetchUserAssets,
  fetch as fetchAllAssets,
} from "services/assets.service";
import { Asset } from "types/Asset";

export type AssetsSelectChangeHandler = (
  assetId: string,
  asset?: Asset
) => void;

const AssetsSelect = ({
  value,
  onChange,
  isRequired,
  label,
  fetchAll = false,
  onLoaded,
}: {
  value?: string;
  onChange?: AssetsSelectChangeHandler;
  isRequired?: boolean;
  label?: string;
  fetchAll?: boolean;
  /** Called with the assets by id once the list has loaded, so a caller can pick one of them. */
  onLoaded?: (assetsById: Record<string, Asset>) => void;
}) => {
  const { t } = useTranslation();
  const [collection, setCollection] = useState<any[]>([]);
  const [assetsById, setAssetsById] = useState<Record<string, Asset>>({});
  const [isLoading, setIsLoading] = useState(false);
  const { companyId } = useParams();
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const handleChange = (selectedValue: string) => {
    if (onChange) {
      onChange(selectedValue, assetsById[selectedValue]);
    }
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        // Use fetchAll to get all company assets, or fetchUserAssets for user-specific
        const assets = companyId
          ? fetchAll
            ? await fetchAllAssets(companyId, {})
            : await fetchUserAssets(companyId)
          : [];
        const assetMap: Record<string, Asset> = {};
        const dataCollection = assets.map((row: Asset) => {
          assetMap[row.id] = row;
          return {
            label: row.name
              ? `${row.name}${row.brand ? ` (${row.brand})` : ""}${
                  row.model ? ` ${row.model}` : ""
                }`
              : row.id,
            value: row.id,
          };
        });
        dataCollection.unshift({ label: " - ", value: "" });
        setAssetsById(assetMap);
        setCollection(dataCollection);
        setIsLoading(false);
        onLoadedRef.current?.(assetMap);
      } catch (e) {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [companyId, fetchAll]);

  return (
    <div>
      <Select
        label={label || t("asset_select")}
        options={collection}
        onChange={handleChange}
        value={value}
        disabled={isLoading}
        requiredIndicator={isRequired ? true : false}
        error={isRequired && !value ? t("asset_is_required") : ""}
      />
    </div>
  );
};

export default AssetsSelect;
